import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { WebsiteHealthSummary } from "contracts/api/website/responses";
import { CONTENT_FITNESS_COVERAGE_STATUS } from "contracts/enums/content-fitness";
import {
  IN_FLIGHT_JOB_STATUSES,
  JOB_STATUS,
  type JobStatus,
} from "contracts/enums/core";
import type { ScanAvailabilityKey } from "../../enums/scan";
import { SCAN_AVAILABILITY_KEY } from "../../enums/scan";

type ScanAvailability = {
  key: ScanAvailabilityKey;
  label: string;
  detail: string;
};

const COPY: Record<ScanAvailabilityKey, Omit<ScanAvailability, "key">> = {
  not_started: {
    label: "No scan started",
    detail: "No scan attempt or completed scored result is available.",
  },
  in_progress: {
    label: "Scan in progress",
    detail: "The latest scan is still running.",
  },
  scored: {
    label: "Completed scored run available",
    detail: "Content Fitness is based on the latest completed scored run.",
  },
  partial: {
    label: "Completed with partial coverage",
    detail: "Content Fitness reflects only the pages and criteria assessed.",
  },
  failed_previous_score: {
    label: "Latest attempt failed, previous scored result available",
    detail:
      "The displayed Content Fitness result comes from the previous completed scored run.",
  },
  failed_no_score: {
    label: "Latest attempt failed, no scored result available",
    detail:
      "The latest scan failed and no completed Content Fitness result exists.",
  },
  completed_without_score: {
    label: "Completed without scored data",
    detail:
      "The latest completed scan did not produce a Content Fitness score.",
  },
};

function availability(key: ScanAvailabilityKey): ScanAvailability {
  return { key, ...COPY[key] };
}

function isRunning(status: JobStatus | null | undefined): boolean {
  return status ? IN_FLIGHT_JOB_STATUSES.includes(status) : false;
}

export function overviewScanAvailability(
  data: OverviewResponse,
): ScanAvailability {
  const latestStatus = data.latestAttempt?.status ?? null;
  const hasScore = data.signal.score !== null;
  if (isRunning(latestStatus))
    return availability(SCAN_AVAILABILITY_KEY.IN_PROGRESS);
  if (latestStatus === JOB_STATUS.FAILED) {
    return availability(
      hasScore
        ? SCAN_AVAILABILITY_KEY.FAILED_PREVIOUS_SCORE
        : SCAN_AVAILABILITY_KEY.FAILED_NO_SCORE,
    );
  }
  if (!data.latestAttempt) {
    return availability(
      hasScore
        ? SCAN_AVAILABILITY_KEY.SCORED
        : SCAN_AVAILABILITY_KEY.NOT_STARTED,
    );
  }
  if (!hasScore)
    return availability(SCAN_AVAILABILITY_KEY.COMPLETED_WITHOUT_SCORE);
  const coverage = data.lastRun?.contentFitnessCoverage;
  if (
    coverage &&
    (coverage.assessedPages < coverage.capturedPages ||
      coverage.scoredPages < coverage.capturedPages)
  ) {
    return availability(SCAN_AVAILABILITY_KEY.PARTIAL);
  }
  return availability(SCAN_AVAILABILITY_KEY.SCORED);
}

export function websiteScanAvailability(
  summary: WebsiteHealthSummary,
): ScanAvailability {
  if (summary.activeRun || isRunning(summary.lastRunStatus)) {
    return availability(SCAN_AVAILABILITY_KEY.IN_PROGRESS);
  }
  const hasScore = summary.signalScore !== null;
  if (summary.lastRunStatus === JOB_STATUS.FAILED) {
    return availability(
      hasScore
        ? SCAN_AVAILABILITY_KEY.FAILED_PREVIOUS_SCORE
        : SCAN_AVAILABILITY_KEY.FAILED_NO_SCORE,
    );
  }
  if (!summary.lastRunStatus) {
    return availability(
      hasScore
        ? SCAN_AVAILABILITY_KEY.SCORED
        : SCAN_AVAILABILITY_KEY.NOT_STARTED,
    );
  }
  if (!hasScore)
    return availability(SCAN_AVAILABILITY_KEY.COMPLETED_WITHOUT_SCORE);
  if (
    summary.scoreRunCoverageStatus === CONTENT_FITNESS_COVERAGE_STATUS.PARTIAL
  ) {
    return availability(SCAN_AVAILABILITY_KEY.PARTIAL);
  }
  return availability(SCAN_AVAILABILITY_KEY.SCORED);
}
